/**
 * <rack-builder>
 *
 * Multi-tab product configurator. Tabs beyond BASICS are data-driven from
 * variant-level metafields under the `build` namespace, all typed as
 * list.variant_reference. Category name === metafield key === DOM data-attr:
 *   build.rack_attachments   → tab "rack_attachments"
 *   build.cable_attachments  → tab "cable_attachments"
 *   build.benches            → tab "benches"
 *   build.barbells           → tab "barbells"
 *   build.weight_plates      → tab "weight_plates"
 *
 * The Liquid layer always renders every tab; updateTabVisibility() hides
 * any tab whose current-variant list is empty.
 *
 * On submit, a single POST /cart/add.js request adds:
 *   [parent variant, ...selected attachments from all categories]
 */
class RackBuilder extends HTMLElement {
  constructor() {
    super();
    this.product = null;
    this.attachmentsByCategory = {}; // { rack: {variantId: [...]}, cable: {...} }
    this.selectedOptions = []; // e.g. ["Black", "94\""]
    this.currentVariant = null; // {id, price, ...} or null when combination unavailable
    this.selection = new Map(); // attachment variantId -> qty (shared across categories)
    this.activeTab = 'basics';
    this.moneyFormat = window.Shopify?.money_format || '${{amount}}';
  }

  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this.trigger = this.querySelector('.rack-builder__trigger');
    this.dataScript = this.querySelector('[data-rack-builder-data]');

    // <modal-dialog>.connectedCallback() moves the modal to <body>.
    const sectionId = this.dataset.sectionId;
    this.modal = document.getElementById(`RackBuilderModal-${sectionId}`)
      || this.querySelector('modal-dialog');
    if (!this.modal) return;

    this.tabs = this.modal.querySelectorAll('[data-rack-builder-tab]');
    this.panels = this.modal.querySelectorAll('[data-rack-builder-panel]');
    // The initial active tab is determined by Liquid (basics is omitted for
    // single-variant products). Fall back to the first rendered tab.
    const initialActiveTab = this.modal.querySelector('[data-rack-builder-tab].is-active')
      || this.tabs[0];
    if (initialActiveTab) this.activeTab = initialActiveTab.dataset.rackBuilderTab;
    this.basicsHost = this.modal.querySelector('[data-rack-builder-basics]');
    this.unavailableEl = this.modal.querySelector('[data-rack-builder-unavailable]');
    // Category-scoped list/empty elements (one per attachment tab).
    this.listByCategory = {};
    this.emptyByCategory = {};
    this.modal.querySelectorAll('[data-rack-builder-list]').forEach((el) => {
      this.listByCategory[el.dataset.rackBuilderList] = el;
    });
    this.modal.querySelectorAll('[data-rack-builder-empty]').forEach((el) => {
      this.emptyByCategory[el.dataset.rackBuilderEmpty] = el;
    });
    this.countEl = this.modal.querySelector('[data-rack-builder-count]');
    this.totalEl = this.modal.querySelector('[data-rack-builder-total]');
    this.submitBtn = this.modal.querySelector('[data-rack-builder-submit]');
    this.submitLabel = this.modal.querySelector('[data-rack-builder-submit-label]');
    this.spinnerEl = this.modal.querySelector('.rack-builder-modal__spinner');
    this.mediaEl = this.modal.querySelector('[data-rack-builder-media]');
    this.imageEl = this.modal.querySelector('[data-rack-builder-image]');
    this.availabilityEl = this.modal.querySelector('[data-rack-builder-availability]');
    this.availabilityStatusEl = this.modal.querySelector('[data-rack-builder-availability-status]');
    this.availabilityEstimateEl = this.modal.querySelector('[data-rack-builder-availability-estimate]');
    this.zoom = 1;

    try {
      const raw = JSON.parse(this.dataScript.textContent.trim());
      this.product = raw.product || {};
      this.attachmentsByCategory = raw.attachments_by_category || {};
      this.categoryModes = raw.category_modes || {};
    } catch (e) {
      console.warn('[rack-builder] failed to parse data payload', e);
      return;
    }

    // Initial variant: from URL, then from data attribute, then first variant.
    const url = new URL(window.location.href);
    const urlVariantId = url.searchParams.get('variant');
    const initialId = urlVariantId || this.dataset.initialVariantId
      || (this.product.variants && this.product.variants[0]?.id);
    this.currentVariant = this.findVariantById(initialId) || this.product.variants?.[0] || null;
    this.selectedOptions = this.currentVariant ? [...this.currentVariant.options] : [];

    this.trigger?.addEventListener('click', () => this.openModal());

    // Attach listeners to the modal (it was reparented out of <rack-builder>)
    this.modal.addEventListener('click', this.onClick.bind(this));
    this.modal.addEventListener('change', this.onChange.bind(this));
    this.modal.addEventListener('input', this.onInput.bind(this));

    this.renderBasics();
    this.rebuildAttachmentsList();
    this.updateFooter();
    this.updateMedia();

    // Subscribe to theme's variant-change pub/sub if available (main page picker)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChangeFromPage.bind(this));
    }
  }

  attachmentCategories() {
    return Object.keys(this.listByCategory);
  }

  disconnectedCallback() {
    if (this.variantChangeUnsub) this.variantChangeUnsub();
  }

  openModal() {
    if (this.modal && typeof this.modal.show === 'function') {
      this.modal.show(this.trigger);
    }
  }

  handleVariantChangeFromPage(event) {
    // Reflect main-page variant picker changes into the modal.
    const nextId = event?.data?.variant?.id;
    if (!nextId) return;
    const key = String(nextId);
    if (this.currentVariant && String(this.currentVariant.id) === key) return;
    const next = this.findVariantById(key);
    if (!next) return;
    this.currentVariant = next;
    this.selectedOptions = [...next.options];
    this.selection.clear();
    this.renderBasics();
    this.rebuildAttachmentsList();
    this.updateFooter();
    this.updateMedia();
  }

  findVariantById(id) {
    if (id == null) return null;
    const key = String(id);
    return (this.product.variants || []).find((v) => String(v.id) === key) || null;
  }

  findVariantByOptions(options) {
    return (this.product.variants || []).find((v) =>
      v.options.length === options.length && v.options.every((o, i) => o === options[i])
    ) || null;
  }

  findAvailableVariantByPrefix(prefixValues) {
    return (this.product.variants || []).find(
      (v) => v.available && prefixValues.every((val, i) => val === v.options[i])
    ) || null;
  }

  // ---------------------------------------------------------------- BASICS

  renderBasics() {
    if (!this.basicsHost) return;
    const options = this.product.options || [];

    const basePriceHtml = this.currentVariant ? priceHtml(this.currentVariant) : '';
    const productTitle = this.product.title || '';
    const selectionCard = `
      <div class="rack-builder-basics__selection">
        <span class="rack-builder-basics__selection-check" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </span>
        <div class="rack-builder-basics__selection-text">
          <p class="rack-builder-basics__selection-title">${escapeHtml(productTitle)}</p>
          <p class="rack-builder-basics__selection-sub">Base configuration</p>
        </div>
        ${basePriceHtml ? `<span class="rack-builder-basics__selection-price">${basePriceHtml}</span>` : ''}
      </div>
    `;

    if (options.length === 0 || this.product.has_only_default_variant) {
      const nextCards = this.attachmentCategories()
        .filter((cat) => this.itemsForCurrentVariant(cat).length > 0)
        .map((cat) => {
          const tabBtn = this.modal.querySelector(`[data-rack-builder-tab="${cat}"]`);
          const label = tabBtn ? tabBtn.textContent.trim() : cat;
          const count = this.itemsForCurrentVariant(cat).length;
          const countLabel = `${count} option${count === 1 ? '' : 's'}`;
          return `
            <button
              type="button"
              class="rack-builder-basics__next-card"
              data-rack-builder-tab="${escapeAttr(cat)}"
            >
              <span class="rack-builder-basics__next-text">
                <span class="rack-builder-basics__next-title">${escapeHtml(label)}</span>
                <span class="rack-builder-basics__next-count">${escapeHtml(countLabel)}</span>
              </span>
              <span class="rack-builder-basics__next-arrow" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </span>
            </button>
          `;
        }).join('');
      const nextHtml = nextCards
        ? `
          <div class="rack-builder-basics__next">
            <p class="rack-builder-basics__next-eyebrow">Enhance your build</p>
            <div class="rack-builder-basics__next-grid">${nextCards}</div>
          </div>
        `
        : '';

      this.basicsHost.innerHTML = `${selectionCard}${nextHtml}`;
      return;
    }

    const html = options.map((opt) => {
      const optIdx = opt.position - 1;
      const selected = this.selectedOptions[optIdx];
      // Only render values that can form an available variant with the values
      // already chosen for the EARLIER options. Incompatible values are hidden
      // entirely (not disabled). Later options are free to be re-resolved
      // (cascaded) on click, so a single pick never silently remaps an earlier
      // option the user already chose.
      const valuesHtml = opt.values
        .filter((val) => {
          const prefix = this.selectedOptions.slice(0, optIdx);
          prefix.push(val);
          return !!this.findAvailableVariantByPrefix(prefix);
        })
        .map((val) => {
          const isSelected = selected === val;
          const cls = ['rack-builder-basics__value', isSelected ? 'is-selected' : '']
            .filter(Boolean)
            .join(' ');
          return `
            <button
              type="button"
              class="${cls}"
              data-option-position="${opt.position}"
              data-option-value="${escapeAttr(val)}"
            >${escapeHtml(val)}</button>
          `;
        })
        .join('');
      return `
        <fieldset class="rack-builder-basics__group">
          <legend class="rack-builder-basics__legend">
            <span class="rack-builder-basics__label">${escapeHtml(opt.name)}</span>
            <span class="rack-builder-basics__selected">${escapeHtml(selected ?? '')}</span>
          </legend>
          <div class="rack-builder-basics__values">${valuesHtml}</div>
        </fieldset>
      `;
    }).join('');
    this.basicsHost.innerHTML = `${selectionCard}${html}`;

    if (this.unavailableEl) this.unavailableEl.hidden = !!this.currentVariant;
  }

  updateMedia() {
    if (!this.imageEl || !this.currentVariant) return;
    const src = this.currentVariant.featured_image || this.product.featured_image;
    if (!src) return;
    if (this.imageEl.tagName === 'IMG') {
      if (this.imageEl.getAttribute('src') !== src) this.imageEl.setAttribute('src', src);
    } else {
      // Replace placeholder div with an <img> once we have a source
      const img = document.createElement('img');
      img.className = 'rack-builder-modal__media-img';
      img.setAttribute('data-rack-builder-image', '');
      img.src = src;
      img.alt = this.product.title || '';
      img.loading = 'lazy';
      this.imageEl.replaceWith(img);
      this.imageEl = img;
    }
  }

  setZoom(delta) {
    this.zoom = Math.max(1, Math.min(2.5, (this.zoom || 1) + delta * 0.2));
    if (this.mediaEl) this.mediaEl.style.setProperty('--rb-zoom', String(this.zoom));
  }

  handleBasicsClick(btn) {
    const pos = parseInt(btn.dataset.optionPosition, 10);
    const value = btn.dataset.optionValue;
    if (!pos || value == null) return;
    const idx = pos - 1;
    if (this.selectedOptions[idx] === value) return;
    this.selectedOptions[idx] = value;

    // Changing this option can invalidate the values already chosen for the
    // later options. Re-resolve them forward (first available match) instead
    // of silently remapping earlier options the user just picked.
    this.cascadeOptionsFrom(idx);

    const variant = this.findVariantByOptions(this.selectedOptions);
    this.currentVariant = variant && variant.available ? variant : null;
    // Attachment selections don't survive a variant change (list differs).
    this.selection.clear();

    this.renderBasics();
    this.rebuildAttachmentsList();
    this.updateFooter();
    this.updateMedia();

    // The clicked button was destroyed by the re-render. Move focus to the
    // newly selected value of the same option group so keyboard users can
    // keep tabbing from a stable position.
    const newSelected = this.basicsHost?.querySelector(
      `.rack-builder-basics__value.is-selected[data-option-position="${pos}"]`
    );
    newSelected?.focus();
  }

  // For every option after startIdx (0-based), keep its current value only if
  // a matching available variant still exists for the prefix ending at it.
  // Otherwise replace it with the first available variant's value. Incompatible
  // values are hidden by renderBasics, so no extra messaging is needed.
  cascadeOptionsFrom(startIdx) {
    const variants = this.product.variants || [];
    const optionCount = this.product.options?.length || 0;

    for (let j = startIdx + 1; j < optionCount; j++) {
      const prefixLength = j + 1;
      const prefixValid = variants.some(
        (v) => v.available && v.options.slice(0, prefixLength).every((o, i) => o === this.selectedOptions[i])
      );
      if (prefixValid) continue;

      const replacement = variants.find(
        (v) => v.available && v.options.slice(0, j).every((o, i) => o === this.selectedOptions[i])
      );
      if (replacement) {
        this.selectedOptions[j] = replacement.options[j];
      }
    }
  }

  // ----------------------------------------------------------- ATTACHMENTS

  rebuildAttachmentsList() {
    const key = this.currentVariant ? String(this.currentVariant.id) : null;
    for (const category of this.attachmentCategories()) {
      const listEl = this.listByCategory[category];
      const emptyEl = this.emptyByCategory[category];
      if (!listEl) continue;
      const items = (key && this.attachmentsByCategory[category]?.[key]) || [];
      listEl.innerHTML = '';
      // Empty message is a fallback; the tab itself is hidden when size is 0
      // via updateTabVisibility() below, so users normally never see it.
      if (items.length === 0) {
        if (emptyEl) emptyEl.hidden = false;
      } else {
        if (emptyEl) emptyEl.hidden = true;
        const mode = this.categoryModes[category] || 'variant_pick';
        listEl.dataset.mode = mode;
        const frag = document.createDocumentFragment();
        for (const it of items) frag.appendChild(this.renderItem(it, category, mode));
        listEl.appendChild(frag);
      }
    }
    this.updateTabVisibility();
  }

  renderItem(item, category, mode) {
    switch (mode) {
      case 'product_pick': return this.renderProductPickItem(item, category);
      case 'product_grid': return this.renderProductGridItem(item, category);
      case 'variant_pick':
      default:             return this.renderAttachmentItem(item, category);
    }
  }

  updateTabVisibility() {
    if (!this.tabs || !this.panels) return;
    // Hide any attachment tab whose current-variant list is empty.
    for (const category of this.attachmentCategories()) {
      const tabBtn = this.modal.querySelector(`[data-rack-builder-tab="${category}"]`);
      const panelEl = this.modal.querySelector(`[data-rack-builder-panel="${category}"]`);
      const isEmpty = this.itemsForCurrentVariant(category).length === 0;
      if (tabBtn) tabBtn.hidden = isEmpty;
      if (panelEl && isEmpty) {
        panelEl.classList.remove('is-active');
        panelEl.hidden = true;
      }
    }
    // If the active tab is now hidden, jump to the first visible tab.
    const activeEl = this.modal.querySelector(`[data-rack-builder-tab="${this.activeTab}"]`);
    if (!activeEl || activeEl.hidden) {
      const firstVisible = Array.from(this.tabs).find((t) => !t.hidden);
      if (firstVisible) {
        // setTab bails when name === activeTab, so reset first.
        this.activeTab = null;
        this.setTab(firstVisible.dataset.rackBuilderTab);
      }
    }
  }

  itemsForCurrentVariant(category) {
    const key = this.currentVariant ? String(this.currentVariant.id) : null;
    if (!key) return [];
    return this.attachmentsByCategory[category]?.[key] || [];
  }

  findVariantData(variantId) {
    // Returns { price, category } for a selected line item — works across all
    // rendering modes (variant_pick items are variants themselves;
    // product_pick / product_grid items are products with nested .variants).
    // `price` reflects the auto-discounted price when custom.percentage_off is set.
    const key = String(variantId);
    for (const category of this.attachmentCategories()) {
      const items = this.itemsForCurrentVariant(category);
      const mode = this.categoryModes[category] || 'variant_pick';
      if (mode === 'variant_pick') {
        const match = items.find((i) => String(i.id) === key);
        if (match) return { price: effectivePrice(match), category };
      } else {
        for (const p of items) {
          const v = (p.variants || []).find((pv) => String(pv.id) === key);
          if (v) return { price: effectivePrice(v), category };
        }
      }
    }
    return null;
  }

  renderAttachmentItem(item, category) {
    const li = document.createElement('li');
    li.className = 'rack-builder-item';
    li.dataset.mode = 'variant_pick';
    li.dataset.variantId = String(item.id);
    if (category) li.dataset.category = category;

    const disabled = item.available === false;
    const priceMarkup = priceHtml(item);

    const imgHtml = item.image
      ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.title)}" width="80" height="80" loading="lazy">`
      : `<span class="rack-builder-item__image-placeholder" aria-hidden="true"></span>`;

    const variantLine = !item.has_only_default_variant && item.variant_title && item.variant_title !== 'Default Title'
      ? `<span class="rack-builder-item__variant">${escapeHtml(item.variant_title)}</span>`
      : '';

    li.innerHTML = `
      <label class="rack-builder-item__label">
        <input type="checkbox" class="rack-builder-item__checkbox"
               value="${item.id}" ${disabled ? 'disabled' : ''} data-rack-builder-checkbox>
        <span class="rack-builder-item__media">${imgHtml}</span>
        <span class="rack-builder-item__info">
          <span class="rack-builder-item__title">${escapeHtml(item.title)}</span>
          ${variantLine}
          <span class="rack-builder-item__price">${priceMarkup}</span>
          ${disabled ? `<span class="rack-builder-item__oos">Sold out</span>` : ''}
        </span>
        <span class="rack-builder-item__qty" data-rack-builder-qty-wrap ${disabled ? 'hidden' : ''}>
          <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="-1" aria-label="Decrease quantity">−</button>
          <input type="number" class="rack-builder-item__qty-input" value="0" min="0" step="1" data-rack-builder-qty>
          <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="1" aria-label="Increase quantity">+</button>
        </span>
      </label>
    `;
    return li;
  }

  renderProductPickItem(item, category) {
    const li = document.createElement('li');
    li.className = 'rack-builder-item rack-builder-item--product-pick';
    li.dataset.mode = 'product_pick';
    li.dataset.productId = String(item.product_id);
    if (category) li.dataset.category = category;

    const variants = item.variants || [];
    const firstAvailable = variants.find((v) => v.available) || variants[0];
    const disabled = !firstAvailable || !firstAvailable.available;
    li.dataset.selectedVariantId = firstAvailable ? String(firstAvailable.id) : '';

    const showPicker = !item.has_only_default_variant && variants.length > 1;
    const swatches = item.swatches || {};

    // Build one button per variant. If any of the variant's option values has
    // a swatch entry, we render a color/image chip; otherwise a text button.
    let pickerHtml = '';
    if (showPicker) {
      const buttons = variants.map((v) => {
        let swatch = null;
        for (const optVal of (v.options || [])) {
          if (swatches[optVal]) { swatch = swatches[optVal]; break; }
        }
        const isSelected = v === firstAvailable;
        const isImageSwatch = swatch && typeof swatch === 'string' && !swatch.startsWith('rgb');
        const style = swatch
          ? isImageSwatch
            ? `--rb-swatch: url('${escapeAttr(swatch)}');`
            : `--rb-swatch: ${escapeAttr(swatch)};`
          : '';
        const cls = [
          swatch ? 'rack-builder-item__swatch' : 'rack-builder-item__variant-btn',
          isSelected ? 'is-selected' : '',
          v.available ? '' : 'is-unavailable',
        ].filter(Boolean).join(' ');
        return `
          <button
            type="button"
            class="${cls}"
            data-rack-builder-variant-btn
            data-variant-id="${v.id}"
            title="${escapeAttr(v.title)}"
            ${style ? `style="${style}"` : ''}
            ${v.available ? '' : 'aria-disabled="true"'}
          >${swatch ? `<span class="visually-hidden">${escapeHtml(v.title)}</span>` : escapeHtml(v.title)}</button>
        `;
      }).join('');
      pickerHtml = `<div class="rack-builder-item__variants-picker" role="radiogroup" aria-label="Select ${escapeAttr(item.title)} variant">${buttons}</div>`;
    }

    const initialSrc = (firstAvailable?.image) || item.image || '';
    const imgHtml = initialSrc
      ? `<img data-rack-builder-item-image data-fallback-src="${escapeAttr(item.image || '')}" src="${escapeAttr(initialSrc)}" alt="${escapeAttr(item.title)}" width="80" height="80" loading="lazy">`
      : `<span class="rack-builder-item__image-placeholder" aria-hidden="true"></span>`;

    const priceMarkup = firstAvailable ? priceHtml(firstAvailable) : escapeHtml(item.price_min_formatted || '');

    li.innerHTML = `
      <label class="rack-builder-item__label">
        <input type="checkbox" class="rack-builder-item__checkbox" ${disabled ? 'disabled' : ''} data-rack-builder-checkbox>
        <span class="rack-builder-item__media">${imgHtml}</span>
        <span class="rack-builder-item__info">
          <span class="rack-builder-item__title">${escapeHtml(item.title)}</span>
          ${pickerHtml}
          <span class="rack-builder-item__price" data-rack-builder-price>${priceMarkup}</span>
          ${disabled ? `<span class="rack-builder-item__oos">Sold out</span>` : ''}
        </span>
        <span class="rack-builder-item__qty" ${disabled ? 'hidden' : ''}>
          <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="-1" aria-label="Decrease quantity">−</button>
          <input type="number" class="rack-builder-item__qty-input" value="0" min="0" step="1" data-rack-builder-qty>
          <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="1" aria-label="Increase quantity">+</button>
        </span>
      </label>
    `;
    return li;
  }

  handleVariantBtnClick(btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const li = btn.closest('.rack-builder-item');
    if (!li) return;
    const oldId = li.dataset.selectedVariantId;
    const newId = btn.dataset.variantId;
    li.dataset.selectedVariantId = newId;

    // Toggle picker button UI
    li.querySelectorAll('[data-rack-builder-variant-btn]').forEach((b) => {
      b.classList.toggle('is-selected', b === btn);
    });

    // Look up the chosen variant for price + image updates.
    const product = this.itemsForCurrentVariant(li.dataset.category || '')
      .find((p) => String(p.product_id) === li.dataset.productId);
    const chosen = product?.variants?.find((v) => String(v.id) === String(newId));

    const priceEl = li.querySelector('[data-rack-builder-price]');
    if (priceEl && chosen) priceEl.innerHTML = priceHtml(chosen);

    // Swap image: use variant's own featured_image, or fall back to product image.
    const imgEl = li.querySelector('[data-rack-builder-item-image]');
    if (imgEl) {
      const src = chosen?.image || imgEl.dataset.fallbackSrc || '';
      if (src && imgEl.getAttribute('src') !== src) imgEl.setAttribute('src', src);
    }

    // Migrate selection state on the variantId key.
    if (oldId && oldId !== newId) this.selection.delete(String(oldId));

    // Picking a variant should count as "selected" — auto-bump qty 0 → 1.
    const qtyInput = li.querySelector('[data-rack-builder-qty]');
    if (qtyInput) {
      const currentQty = parseInt(qtyInput.value, 10) || 0;
      if (currentQty < 1) {
        qtyInput.value = '1';
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        return; // onInput handles selection.set + checkbox + updateFooter
      }
      // Already selected → move the existing qty to the new variantId.
      this.selection.set(String(newId), currentQty);
      this.updateFooter();
    }
  }

  renderProductGridItem(item, category) {
    const li = document.createElement('li');
    li.className = 'rack-builder-item rack-builder-item--product-grid';
    li.dataset.mode = 'product_grid';
    li.dataset.productId = String(item.product_id);
    if (category) li.dataset.category = category;

    const variantCards = (item.variants || []).map((v) => {
      const disabled = !v.available;
      const cardImg = v.image || item.image;
      const cardImgHtml = cardImg
        ? `<img src="${escapeAttr(cardImg)}" alt="${escapeAttr(v.title)}" width="120" height="120" loading="lazy">`
        : `<span class="rack-builder-variant-card__image-placeholder" aria-hidden="true"></span>`;
      const priceMarkup = priceHtml(v);
      return `
        <li class="rack-builder-variant-card ${disabled ? 'is-unavailable' : ''}" data-variant-id="${v.id}">
          <span class="rack-builder-variant-card__media">${cardImgHtml}</span>
          <span class="rack-builder-variant-card__title">${escapeHtml(v.title)}${disabled ? ' (Sold out)' : ''}</span>
          <span class="rack-builder-variant-card__price">${priceMarkup}</span>
          <span class="rack-builder-item__qty" ${disabled ? 'hidden' : ''}>
            <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="-1" aria-label="Decrease quantity">−</button>
            <input type="number" class="rack-builder-item__qty-input" value="0" min="0" step="1" data-rack-builder-qty>
            <button type="button" class="rack-builder-item__qty-btn" data-qty-delta="1" aria-label="Increase quantity">+</button>
          </span>
        </li>
      `;
    }).join('');

    li.innerHTML = `
      <h3 class="rack-builder-item__section-title">${escapeHtml(item.title)}</h3>
      <ul class="rack-builder-variant-cards">${variantCards}</ul>
    `;
    return li;
  }

  // ------------------------------------------------------------ delegation

  onClick(event) {
    const zoomBtn = event.target.closest('[data-zoom-delta]');
    if (zoomBtn) {
      event.preventDefault();
      this.setZoom(parseInt(zoomBtn.dataset.zoomDelta, 10) || 0);
      return;
    }
    const tab = event.target.closest('[data-rack-builder-tab]');
    if (tab) {
      event.preventDefault();
      this.setTab(tab.dataset.rackBuilderTab);
      return;
    }
    const basicsBtn = event.target.closest('[data-option-position]');
    if (basicsBtn) {
      event.preventDefault();
      this.handleBasicsClick(basicsBtn);
      return;
    }
    const variantBtn = event.target.closest('[data-rack-builder-variant-btn]');
    if (variantBtn) {
      event.preventDefault();
      this.handleVariantBtnClick(variantBtn);
      return;
    }
    // Click anywhere on a product_grid variant card (except the qty controls
    // themselves) toggles it between qty 0 and qty 1.
    const card = event.target.closest('.rack-builder-variant-card');
    if (card
        && !card.classList.contains('is-unavailable')
        && !event.target.closest('[data-qty-delta]')
        && !event.target.closest('[data-rack-builder-qty]')) {
      event.preventDefault();
      const input = card.querySelector('[data-rack-builder-qty]');
      if (input) {
        const current = parseInt(input.value, 10) || 0;
        input.value = current > 0 ? '0' : '1';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }
    const delta = event.target.closest('[data-qty-delta]');
    if (delta) {
      event.preventDefault();
      // The qty input lives as a sibling of the button inside the same wrapper.
      const input = delta.parentElement?.querySelector('[data-rack-builder-qty]');
      if (!input) return;
      const min = parseInt(input.min, 10) || 0;
      const current = parseInt(input.value, 10);
      const start = isNaN(current) ? min : current;
      const next = Math.max(min, start + parseInt(delta.dataset.qtyDelta, 10));
      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    const submit = event.target.closest('[data-rack-builder-submit]');
    if (submit) {
      event.preventDefault();
      this.submit();
    }
  }

  onChange(event) {
    const cb = event.target.closest('[data-rack-builder-checkbox]');
    if (!cb) return;
    const li = cb.closest('.rack-builder-item');
    if (!li) return;
    // Checkbox is a UI shortcut — actual selection state is driven by the qty input.
    // Toggling the checkbox just changes qty to 0 or 1, then onInput handles the rest.
    const qtyInput = li.querySelector('[data-rack-builder-qty]');
    if (!qtyInput) return;
    const current = parseInt(qtyInput.value, 10) || 0;
    if (cb.checked && current < 1) qtyInput.value = '1';
    else if (!cb.checked && current > 0) qtyInput.value = '0';
    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onInput(event) {
    const input = event.target.closest('[data-rack-builder-qty]');
    if (!input) return;
    const min = parseInt(input.min, 10) || 0;
    let qty = parseInt(input.value, 10);
    if (isNaN(qty)) qty = min;
    if (qty < min) { qty = min; input.value = String(min); }

    // product_grid: qty lives on a per-variant card; qty > 0 == selected.
    const card = input.closest('.rack-builder-variant-card');
    if (card) {
      const id = card.dataset.variantId;
      if (!id) return;
      if (qty > 0) this.selection.set(String(id), qty);
      else this.selection.delete(String(id));
      card.classList.toggle('is-selected', qty > 0);
      this.updateFooter();
      return;
    }

    // variant_pick / product_pick: qty is the source of truth.
    //   qty > 0  → auto-check the checkbox + add to selection
    //   qty == 0 → auto-uncheck + remove
    const li = input.closest('.rack-builder-item');
    if (!li) return;
    const mode = li.dataset.mode || 'variant_pick';
    const id = mode === 'product_pick' ? li.dataset.selectedVariantId : li.dataset.variantId;
    if (!id) return;
    const cb = li.querySelector('[data-rack-builder-checkbox]');
    const shouldBeChecked = qty > 0;
    if (cb && cb.checked !== shouldBeChecked) cb.checked = shouldBeChecked;
    li.classList.toggle('is-selected', shouldBeChecked);
    if (shouldBeChecked) this.selection.set(String(id), qty);
    else this.selection.delete(String(id));
    this.updateFooter();
  }

  setTab(name) {
    if (this.activeTab === name) return;
    this.activeTab = name;
    this.tabs?.forEach((t) => {
      const on = t.dataset.rackBuilderTab === name;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
    this.panels?.forEach((p) => {
      const on = p.dataset.rackBuilderPanel === name;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
  }

  // -------------------------------------------------------------- footer

  updateFooter() {
    if (!this.countEl || !this.totalEl || !this.submitBtn) return;

    const attachmentEntries = Array.from(this.selection.entries());
    const attachmentQty = attachmentEntries.reduce((s, [, q]) => s + q, 0);

    // Sum attachment prices across all categories for the current variant.
    let cents = 0;
    for (const [id, qty] of attachmentEntries) {
      const found = this.findVariantData(id);
      if (found) cents += (found.price || 0) * qty;
    }
    if (this.currentVariant) cents += effectivePrice(this.currentVariant) || 0;

    if (this.currentVariant) {
      this.countEl.textContent = attachmentQty > 0
        ? `Base + ${attachmentQty} attachment${attachmentQty === 1 ? '' : 's'}`
        : 'Base only';
    } else {
      this.countEl.textContent = 'Unavailable';
    }
    this.totalEl.textContent = cents > 0 ? formatMoney(cents, this.moneyFormat) : '';
    this.submitBtn.disabled = !this.currentVariant || !this.currentVariant.available;
    this.updateAvailability();
  }

  updateAvailability() {
    if (!this.availabilityEl || !this.availabilityStatusEl || !this.submitLabel) return;

    if (!this.currentVariant || !this.currentVariant.available) {
      this.availabilityEl.hidden = true;
      return;
    }

    const v = this.currentVariant;
    const isBackorder = v.inventory_management === 'shopify'
      && v.inventory_policy === 'continue'
      && v.inventory_quantity <= 0;

    const LOW_STOCK_THRESHOLD = 2;
    const isLowStock = v.inventory_management === 'shopify'
      && v.inventory_quantity > 0
      && v.inventory_quantity <= LOW_STOCK_THRESHOLD;

    if (isBackorder) {
      const isPreorder = this.product.lifecycle === 'upcoming';
      this.availabilityEl.hidden = false;
      if (isPreorder) {
        this.availabilityStatusEl.className = 'rack-builder-modal__availability-status rack-builder-modal__availability-status--preorder';
        this.availabilityStatusEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
          <strong>Preorder</strong>
        `;
        this.submitLabel.textContent = 'Preorder';
      } else {
        this.availabilityStatusEl.className = 'rack-builder-modal__availability-status rack-builder-modal__availability-status--backorder';
        this.availabilityStatusEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
          <strong>Backorder</strong>
        `;
        this.submitLabel.textContent = 'Place backorder';
      }
      if (v.restock_date) {
        this.availabilityEstimateEl.hidden = false;
        this.availabilityEstimateEl.textContent = `Estimated to ship by ${v.restock_date}. Date is subject to change due to transit delays.`;
      } else {
        this.availabilityEstimateEl.hidden = true;
      }
    } else if (isLowStock) {
      this.availabilityEl.hidden = false;
      this.availabilityStatusEl.className = 'rack-builder-modal__availability-status rack-builder-modal__availability-status--low-stock';
      this.availabilityStatusEl.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
        <strong>Only ${v.inventory_quantity} left — order soon</strong>
      `;
      this.submitLabel.textContent = 'Add to cart';
      this.availabilityEstimateEl.hidden = true;
    } else {
      this.availabilityEl.hidden = false;
      this.availabilityStatusEl.className = 'rack-builder-modal__availability-status rack-builder-modal__availability-status--in-stock';
      this.availabilityStatusEl.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <strong>In Stock</strong>
      `;
      this.submitLabel.textContent = 'Add to cart';
      this.availabilityEstimateEl.hidden = true;
    }
  }

  // -------------------------------------------------------------- submit

  async submit() {
    if (!this.currentVariant || !this.currentVariant.available) return;
    this.setLoading(true);

    const items = [
      {
        id: Number(this.currentVariant.id),
        quantity: 1,
        properties: {
          _rack_builder: this.dataset.productTitle || 'Rack Builder',
        },
      },
      ...Array.from(this.selection.entries()).map(([id, quantity]) => {
        const found = this.findVariantData(id);
        return {
          id: Number(id),
          quantity,
          properties: {
            _rack_builder: this.dataset.productTitle || 'Rack Builder',
            _rack_builder_role: found?.category || 'attachment',
          },
        };
      }),
      ...(this.currentVariant.auto_add_to_cart || []).map((id) => ({
        id: Number(id),
        quantity: 1,
        properties: {
          _rack_builder: this.dataset.productTitle || 'Rack Builder',
          _rack_builder_role: 'auto_add',
        },
      })),
    ];

    const sectionsEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sectionsToFetch = sectionsEl ? sectionsEl.getSectionsToRender().map((s) => s.id) : [];

    const body = { items };
    if (sectionsToFetch.length) {
      body.sections = sectionsToFetch;
      body.sections_url = window.location.pathname;
    }
    if (sectionsEl) sectionsEl.setActiveElement(document.activeElement);

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (data.status || data.errors) {
        this.showError(data.description || data.message || 'Could not add items');
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'rack-builder',
          productVariantId: this.currentVariant.id,
          cartData: data,
        });
      }
      if (sectionsEl) {
        sectionsEl.renderContents(data);
        if (sectionsEl.classList.contains('is-empty')) sectionsEl.classList.remove('is-empty');
      }

      // Keep selection + qty / checkbox UI state so the shopper can see what
      // was just added. If they hit Add to cart again with the same picks it
      // will add another set — that's the intended behaviour.
      this.flashAdded();
      // Keep the modal open so the shopper can keep configuring / adding.
      // Cart drawer / notification renders above thanks to its own z-index.
      if (!sectionsEl) window.location = window.routes.cart_url;
    } catch (err) {
      console.error('[rack-builder] add failed', err);
      this.showError('Network error — please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  flashAdded() {
    if (!this.submitLabel) return;
    const original = this.submitLabel.dataset.originalLabel || this.submitLabel.textContent;
    this.submitLabel.dataset.originalLabel = original;
    this.submitLabel.textContent = 'Added to cart ✓';
    this.submitBtn?.classList.add('is-added');
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => {
      this.submitLabel.textContent = original;
      this.submitBtn?.classList.remove('is-added');
    }, 1800);
  }

  setLoading(on) {
    this.submitBtn?.classList.toggle('is-loading', !!on);
    if (this.submitLabel) this.submitLabel.hidden = !!on;
    if (this.spinnerEl) this.spinnerEl.hidden = !on;
    if (this.submitBtn) {
      this.submitBtn.disabled = on || !this.currentVariant || !this.currentVariant.available;
    }
  }

  showError(msg) {
    const root = this.modal || this;
    let el = root.querySelector('[data-rack-builder-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'rack-builder-modal__error';
      el.setAttribute('data-rack-builder-error', '');
      root.querySelector('.rack-builder-modal__footer')?.prepend(el);
    }
    el.textContent = msg;
  }
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function escapeAttr(str) {
  return escapeHtml(str);
}
function formatMoney(cents, format) {
  const dollars = (cents / 100).toFixed(2);
  const [intPart, fracPart] = dollars.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amount = `${withCommas}.${fracPart}`;
  return (format || '${{amount}}').replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, amount);
}

// Effective per-unit price in cents: auto discount wins over compare_at sale.
function effectivePrice(item) {
  if (!item) return 0;
  if (item.discounted_price != null) return item.discounted_price;
  return item.price || 0;
}

// Price markup mirrors PDP behavior:
//   auto discount → discounted price + struck-through variant.price
//   compare_at sale → variant.price + struck-through compare_at
//   otherwise → variant.price only
function priceHtml(item) {
  if (!item) return '';
  if (item.discounted_price_formatted) {
    return `${escapeHtml(item.discounted_price_formatted)} <s class="rack-builder-item__compare">${escapeHtml(item.price_formatted)}</s>`;
  }
  if (item.compare_at_price_formatted) {
    return `${escapeHtml(item.price_formatted)} <s class="rack-builder-item__compare">${escapeHtml(item.compare_at_price_formatted)}</s>`;
  }
  return escapeHtml(item.price_formatted || '');
}

customElements.define('rack-builder', RackBuilder);
